import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
    formatMinutesAsHHMM,
    getFlightBlockTimeMinutes,
    getFlightTimeDisplayInfo,
    normalizeMinutes,
} from "../../lib/flightTimingUtils";
import { formatTrackTypeLabel } from "../../lib/trackUtils";

dayjs.extend(utc);

const HOVER_PANEL_VIEWPORT_PADDING = 12;
const HOVER_PANEL_HORIZONTAL_OFFSET = 14;
const HOVER_PANEL_VERTICAL_OFFSET = 16;
const HOVER_PANEL_DEFAULT_WIDTH = 380;
const HOVER_PANEL_DEFAULT_HEIGHT = 320;
const HOVER_PANEL_TRACK_ROW_HEIGHT = 64;
const HOVER_PANEL_VERTICAL_AVOIDANCE_TRACKS = 2;
const HOVER_PANEL_MIN_VERTICAL_AVOIDANCE = 96;
const HOVER_PANEL_NEAR_ANCHOR_OFFSET_TRACKS = 1.25;
const HOVER_PANEL_MIN_NEAR_ANCHOR_OFFSET = 48;
const HOVER_PANEL_MIN_VISIBLE_HEIGHT = 48;
const HOVER_PANEL_VERTICAL_STRATEGY_AVOID_VIEWPORT = "avoid-viewport";
const HOVER_PANEL_VERTICAL_STRATEGY_NEAR_ANCHOR = "near-anchor";

const PUBLICATION_STATUS_LABELS = {
    MATCHED: "已替换",
    INVALID: "发布快照缺失",
    NEW: "发布新增",
    CANCELLED: "发布取消",
    IGNORE: "发布忽略",
    UNMATCHED: "发布快照缺失",
};

const MATCH_STATUS_LABELS = {
    MATCHED: "已匹配",
    INVALID: "匹配失效",
    NEW: "新增",
    CANCELLED: "应取消",
    IGNORE: "忽略",
    UNMATCHED: "待确认",
};

const MATCH_TYPE_LABELS = {
    AUTO: "系统建议",
    MANUAL: "人工决策",
};

const EXECUTION_STATUS_LABELS = {
    SCHEDULED: "计划",
    ACTIVE: "执行中",
    DELAYED: "延误",
    DIVERTED: "备降",
    CANCELLED: "已取消",
    COMPLETED: "已完成",
};

const EXECUTION_STATUS_SAFE_FOR_MATCH = new Set(["SCHEDULED", "ACTIVE", "DELAYED", "DIVERTED"]);

const TRANSACTION_TYPE_LABELS = {
    fuel_request: "燃油",
    permit_request: "许可",
    catering_order: "配餐",
    flight_brief: "Brief",
    crew_transportation: "机组交通",
    hotac: "HOTAC",
};

const MIGRATION_STATUS_LABELS = {
    PENDING_REVIEW: "待复核",
    CONFIRMED: "已确认",
    REJECTED: "已拒绝",
};

const TIME_DIFF_NEUTRAL_THRESHOLD_MINUTES = 1;

const CHANGE_STATUS_LABELS = {
    UNCHANGED: "未变更",
    ADDED: "新增",
    MODIFIED: "已调整",
    DELETED: "已删除",
};

const AIRCRAFT_AVAILABILITY_TYPE_LABELS = {
    SCHEDULED_MAINTENANCE: "计划维修",
    UNSCHEDULED_MAINTENANCE: "非计划维修",
    MODIFICATION: "改装",
    LEASE_RETURN: "租约归还",
    RESERVATION: "预留",
    OTHER: "其他不可用",
};

const AIRCRAFT_AVAILABILITY_STATUS_LABELS = {
    ACTIVE: "生效中",
    COMPLETED: "已完成",
    CANCELLED: "已取消",
};

const TRACK_CONTEXT_LABELS = {
    EDITING: "编辑轨",
    AGENT: "Agent 方案",
    HISTORY: "历史快照",
    REFERENCE: "参考轨",
    LIVE_REFERENCE: "参考基线",
};

function hasValue(value) {
    return value !== undefined && value !== null && value !== "";
}

function toFiniteNumber(value) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
}

function toPositiveNumber(value, fallback) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
}

function clamp(value, min, max) {
    if (max < min) {
        return min;
    }
    return Math.min(Math.max(value, min), max);
}

function normalizeViewportPoint(point) {
    const x = toFiniteNumber(point?.x ?? point?.clientX);
    const y = toFiniteNumber(point?.y ?? point?.clientY);
    return x === null || y === null ? null : { x, y };
}

function normalizeViewportRect(rect) {
    const left = toFiniteNumber(rect?.left ?? rect?.x);
    const top = toFiniteNumber(rect?.top ?? rect?.y);
    const width = toFiniteNumber(rect?.width);
    const height = toFiniteNumber(rect?.height);
    const right = toFiniteNumber(rect?.right) ?? (left !== null && width !== null ? left + width : null);
    const bottom = toFiniteNumber(rect?.bottom) ?? (top !== null && height !== null ? top + height : null);

    if (left === null || top === null || right === null || bottom === null) {
        return null;
    }

    return {
        left,
        top,
        right,
        bottom,
        width: width ?? Math.max(0, right - left),
        height: height ?? Math.max(0, bottom - top),
    };
}

function areViewportRectsEqual(left, right) {
    if (!left || !right) {
        return left === right;
    }
    return left.left === right.left
        && left.top === right.top
        && left.right === right.right
        && left.bottom === right.bottom
        && left.width === right.width
        && left.height === right.height;
}

function getAnchorElementViewportRect(anchorElement) {
    return normalizeViewportRect(anchorElement?.getBoundingClientRect?.());
}

function getBrowserViewportSize() {
    if (typeof window === "undefined") {
        return { width: 1024, height: 768 };
    }
    return {
        width: window.innerWidth || document.documentElement?.clientWidth || 1024,
        height: window.innerHeight || document.documentElement?.clientHeight || 768,
    };
}

function calculateHoverPanelVerticalPlacement({
    anchorTop,
    anchorBottom,
    panelHeight,
    viewportHeight,
    safePadding,
    verticalAvoidance,
    verticalOffset,
}) {
    const aboveAvailable = Math.max(0, anchorTop - verticalAvoidance - verticalOffset - safePadding);
    const belowAvailable = Math.max(0, viewportHeight - safePadding - (anchorBottom + verticalAvoidance + verticalOffset));
    const belowFits = belowAvailable >= panelHeight;
    const aboveFits = aboveAvailable >= panelHeight;
    const placeBelow = belowFits || (!aboveFits && belowAvailable >= aboveAvailable);
    const verticalPlacement = placeBelow ? "below" : "above";
    const verticalAvailable = Math.max(1, placeBelow ? belowAvailable : aboveAvailable);
    const effectivePanelHeight = Math.min(panelHeight, verticalAvailable);
    const reservedPanelHeight = placeBelow ? effectivePanelHeight : verticalAvailable;
    const unclampedTop = placeBelow
        ? anchorBottom + verticalAvoidance + verticalOffset
        : anchorTop - verticalAvoidance - verticalOffset - reservedPanelHeight;
    const top = clamp(unclampedTop, safePadding, viewportHeight - safePadding - reservedPanelHeight);

    return {
        maxHeight: verticalAvailable,
        top,
        verticalPlacement,
    };
}

function calculateNearAnchorHoverPanelVerticalPlacement({
    anchorBottom,
    panelHeight,
    viewportHeight,
    safePadding,
    rowHeight,
}) {
    const verticalAvoidance = Math.max(HOVER_PANEL_MIN_NEAR_ANCHOR_OFFSET, rowHeight * HOVER_PANEL_NEAR_ANCHOR_OFFSET_TRACKS);
    const minVisibleHeight = Math.min(HOVER_PANEL_MIN_VISIBLE_HEIGHT, Math.max(1, panelHeight));
    const latestTopWithVisiblePanel = Math.max(safePadding, viewportHeight - safePadding - minVisibleHeight);
    const top = Math.min(anchorBottom + verticalAvoidance, latestTopWithVisiblePanel);

    return {
        maxHeight: Math.max(1, viewportHeight - safePadding * 2),
        top,
        verticalPlacement: "below",
        verticalAvoidance,
    };
}

export function calculateScheduleDraftTimelineHoverPanelPosition({
    anchorRect,
    viewportPoint,
    panelSize,
    viewportSize,
    trackRowHeight,
    viewportPadding = HOVER_PANEL_VIEWPORT_PADDING,
    horizontalOffset = HOVER_PANEL_HORIZONTAL_OFFSET,
    verticalOffset = HOVER_PANEL_VERTICAL_OFFSET,
    verticalStrategy = HOVER_PANEL_VERTICAL_STRATEGY_AVOID_VIEWPORT,
} = {}) {
    const viewportWidth = toPositiveNumber(viewportSize?.width, 1024);
    const viewportHeight = toPositiveNumber(viewportSize?.height, 768);
    const safePadding = Math.max(0, toPositiveNumber(viewportPadding, HOVER_PANEL_VIEWPORT_PADDING));
    const availableWidth = Math.max(1, viewportWidth - safePadding * 2);
    const panelWidth = Math.min(toPositiveNumber(panelSize?.width, HOVER_PANEL_DEFAULT_WIDTH), availableWidth);
    const panelHeight = Math.min(
        toPositiveNumber(panelSize?.height, HOVER_PANEL_DEFAULT_HEIGHT),
        Math.max(1, viewportHeight - safePadding * 2),
    );
    const rowHeight = toPositiveNumber(trackRowHeight, HOVER_PANEL_TRACK_ROW_HEIGHT);
    const verticalAvoidance = Math.max(HOVER_PANEL_MIN_VERTICAL_AVOIDANCE, rowHeight * HOVER_PANEL_VERTICAL_AVOIDANCE_TRACKS);
    const anchor = normalizeViewportRect(anchorRect);

    if (anchor) {
        const rightLeft = anchor.right + horizontalOffset;
        const leftLeft = anchor.left - horizontalOffset - panelWidth;
        const rightOverflow = rightLeft + panelWidth > viewportWidth - safePadding;
        const canPlaceLeft = leftLeft >= safePadding;
        const horizontalPlacement = rightOverflow && canPlaceLeft ? "left" : "right";
        const left = clamp(
            horizontalPlacement === "left" ? leftLeft : rightLeft,
            safePadding,
            viewportWidth - safePadding - panelWidth,
        );
        const verticalPosition = verticalStrategy === HOVER_PANEL_VERTICAL_STRATEGY_NEAR_ANCHOR
            ? calculateNearAnchorHoverPanelVerticalPlacement({
                anchorBottom: anchor.bottom,
                panelHeight,
                viewportHeight,
                safePadding,
                rowHeight,
            })
            : calculateHoverPanelVerticalPlacement({
                anchorTop: anchor.top,
                anchorBottom: anchor.bottom,
                panelHeight,
                viewportHeight,
                safePadding,
                verticalAvoidance,
                verticalOffset,
            });

        return {
            left: Math.round(left),
            top: Math.round(verticalPosition.top),
            maxHeight: Math.floor(verticalPosition.maxHeight),
            horizontalPlacement,
            verticalPlacement: verticalPosition.verticalPlacement,
            verticalAvoidance,
            width: Math.round(panelWidth),
        };
    }

    const point = normalizeViewportPoint(viewportPoint);
    const anchorX = point?.x ?? viewportWidth - safePadding - panelWidth - horizontalOffset;
    const anchorY = point?.y ?? viewportHeight - safePadding - panelHeight - verticalAvoidance - verticalOffset;

    const rightLeft = anchorX + horizontalOffset;
    const leftLeft = anchorX - horizontalOffset - panelWidth;
    const shouldFlipLeft = rightLeft + panelWidth > viewportWidth - safePadding;
    const horizontalPlacement = shouldFlipLeft ? "left" : "right";
    const left = clamp(
        shouldFlipLeft ? leftLeft : rightLeft,
        safePadding,
        viewportWidth - safePadding - panelWidth,
    );

    const verticalPosition = verticalStrategy === HOVER_PANEL_VERTICAL_STRATEGY_NEAR_ANCHOR
        ? calculateNearAnchorHoverPanelVerticalPlacement({
            anchorBottom: anchorY,
            panelHeight,
            viewportHeight,
            safePadding,
            rowHeight,
        })
        : calculateHoverPanelVerticalPlacement({
            anchorTop: anchorY,
            anchorBottom: anchorY,
            panelHeight,
            viewportHeight,
            safePadding,
            verticalAvoidance,
            verticalOffset,
        });

    return {
        left: Math.round(left),
        top: Math.round(verticalPosition.top),
        maxHeight: Math.floor(verticalPosition.maxHeight),
        horizontalPlacement,
        verticalPlacement: verticalPosition.verticalPlacement,
        verticalAvoidance,
        width: Math.round(panelWidth),
    };
}

function normalizeText(value, fallback = "--") {
    return hasValue(value) ? String(value) : fallback;
}

function formatUtcTime(value) {
    if (!value) {
        return "--";
    }
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? `${parsed.format("MM-DD HH:mm")}Z` : String(value);
}

function getFlightNumber(flight = {}) {
    return flight.flightNumber || flight.flight_number || flight.flightNo || flight.flight_no;
}

function getDepartureAirport(flight = {}) {
    return flight.departureAirport || flight.departure_airport;
}

function getArrivalAirport(flight = {}) {
    return flight.arrivalAirport || flight.arrival_airport;
}

function formatFlightRoute(flight = {}) {
    return `${normalizeText(getDepartureAirport(flight))} -> ${normalizeText(getArrivalAirport(flight))}`;
}

function formatFlightIdentity(flight = {}) {
    return `${normalizeText(getFlightNumber(flight), "未命名航班")} · ${formatFlightRoute(flight)}`;
}

function formatTimeRange(flight, useEffectiveTime = false) {
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime });
    const departureLabel = timeInfo.departureSourceLabel || timeInfo.departureLabel;
    const arrivalLabel = timeInfo.arrivalSourceLabel || timeInfo.arrivalLabel;
    return `${departureLabel} ${formatUtcTime(timeInfo.departureTime)} / ${arrivalLabel} ${formatUtcTime(timeInfo.arrivalTime)}`;
}

function formatTimeContext(flight, useEffectiveTime = false) {
    return `${useEffectiveTime ? "参考有效时刻" : "计划时刻"} ${formatTimeRange(flight, useEffectiveTime)}`;
}

function formatFlightSummary(flight) {
    if (!flight) {
        return null;
    }
    return `${normalizeText(getFlightNumber(flight), "未命名航班")} ${normalizeText(getDepartureAirport(flight))}->${normalizeText(getArrivalAirport(flight))}`;
}

function formatMinutes(value) {
    return formatMinutesAsHHMM(value);
}

function formatCompactMinutes(value) {
    const minutes = normalizeMinutes(value);
    if (minutes === null) {
        return "--";
    }
    const absoluteMinutes = Math.abs(minutes);
    const hours = Math.floor(absoluteMinutes / 60);
    const mins = absoluteMinutes % 60;
    const valueText = hours && mins
        ? `${hours}h${mins}m`
        : hours
            ? `${hours}h`
            : `${mins}m`;
    return minutes < 0 ? `-${valueText}` : valueText;
}

function formatSignedCompactMinutes(value) {
    const minutes = normalizeMinutes(value);
    if (minutes === null) {
        return null;
    }
    if (minutes === 0) {
        return "0m";
    }
    return `${minutes >= 0 ? "+" : ""}${formatCompactMinutes(minutes)}`;
}

function normalizeBadgeText(badge) {
    if (!badge) {
        return null;
    }
    if (typeof badge === "string") {
        return badge;
    }
    return badge.text || badge.label || null;
}

function uniqueTexts(values) {
    return Array.from(new Set(values.filter(Boolean)));
}

function normalizeViolationList(value) {
    if (Array.isArray(value)) {
        return value;
    }
    if (value && typeof value === "object") {
        return Object.values(value).flatMap((entry) => (Array.isArray(entry) ? entry : [entry]));
    }
    return [];
}

function collectRiskLabels(item, flight) {
    const violations = [
        ...normalizeViolationList(flight?.constraint_violations),
        ...normalizeViolationList(flight?.constraintViolations),
        ...normalizeViolationList(flight?.local_validation_issues),
        ...normalizeViolationList(item?.meta?.constraintViolations),
    ];

    return uniqueTexts(violations.map((violation) => (
        violation?.message
        || violation?.label
        || violation?.description
        || violation?.constraint_type
        || violation?.type
        || violation?.code
    )));
}

function getChangeStatusLabel(status) {
    const normalizedStatus = String(status || "").toUpperCase();
    return CHANGE_STATUS_LABELS[normalizedStatus] || null;
}

function getTrackContextBadge(item) {
    const role = item?.meta?.role;
    if (role === "editing") {
        return "编辑侧";
    }
    if (role === "reference") {
        return "参考基线";
    }

    const trackType = item?.meta?.scheduleTrack?.track_type || item?.meta?.referenceSource?.type;
    const normalizedTrackType = String(trackType || "").toUpperCase();
    if (TRACK_CONTEXT_LABELS[normalizedTrackType]) {
        return TRACK_CONTEXT_LABELS[normalizedTrackType];
    }
    if (item?.variant === "editable" || item?.meta?.trackKind === "editable") {
        return "编辑轨";
    }
    if (item?.variant === "reference" || item?.meta?.trackKind === "reference") {
        return item?.meta?.usesEffectiveTime ? "参考基线" : "参考轨";
    }
    return formatTrackTypeLabel(trackType, "") || null;
}

function buildRow(label, value, options = {}) {
    if (!hasValue(value)) {
        return null;
    }
    return {
        key: options.key || `${label}:${String(value)}`,
        label,
        value: String(value),
        tone: options.tone || null,
    };
}

function compactRows(rows) {
    return rows.filter(Boolean);
}

function getMinutesDiff(leftValue, rightValue) {
    if (!leftValue || !rightValue) {
        return null;
    }
    const leftTime = dayjs.utc(leftValue);
    const rightTime = dayjs.utc(rightValue);
    if (!leftTime.isValid() || !rightTime.isValid()) {
        return null;
    }
    return leftTime.diff(rightTime, "minute");
}

function getTimeDiffDirection(minutes) {
    if (minutes === null || minutes === undefined || Math.abs(minutes) <= TIME_DIFF_NEUTRAL_THRESHOLD_MINUTES) {
        return "neutral";
    }
    return minutes > 0 ? "delayed" : "early";
}

function formatRelativeMinutes(minutes) {
    const absoluteMinutes = Math.abs(Number(minutes) || 0);
    const hours = Math.floor(absoluteMinutes / 60);
    const mins = absoluteMinutes % 60;
    if (hours && mins) {
        return `${hours}h${mins}m`;
    }
    if (hours) {
        return `${hours}h`;
    }
    return `${mins}m`;
}

function formatRelativeTimeDiff(minutes) {
    if (minutes === null || minutes === undefined) {
        return "时间待确认";
    }
    const direction = getTimeDiffDirection(minutes);
    if (direction === "neutral") {
        return "时间一致";
    }
    return `${direction === "delayed" ? "延误" : "提前"} ${formatRelativeMinutes(minutes)}`;
}

function formatSignedTimeDiff(minutes) {
    if (minutes === null || minutes === undefined) {
        return null;
    }
    const direction = getTimeDiffDirection(minutes);
    if (direction === "neutral") {
        return null;
    }
    return `${direction === "delayed" ? "+" : "-"}${formatRelativeMinutes(minutes)}`;
}

function formatTimeDiffDetail(minutes, editingLabel, editingTime, referenceLabel, referenceTime) {
    const diffLabel = formatRelativeTimeDiff(minutes);
    if (minutes === null || minutes === undefined) {
        return diffLabel;
    }
    return `${diffLabel}（草稿 ${editingLabel} ${formatUtcTime(editingTime)} / 基线 ${referenceLabel} ${formatUtcTime(referenceTime)}）`;
}

const COMPARE_OVERVIEW_TEXT_FIELDS = [
    { key: "flightNumber", label: "航班号" },
    { key: "departureAirport", label: "始发地" },
    { key: "arrivalAirport", label: "目的地" },
];

const COMPARE_OVERVIEW_TIME_FIELDS = [
    { key: "departureTime", label: "起飞" },
    { key: "arrivalTime", label: "落地" },
];

function buildCompareOverviewValues(flight, useEffectiveTime = false) {
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime });
    return {
        flightNumber: normalizeText(getFlightNumber(flight)),
        departureAirport: normalizeText(getDepartureAirport(flight)),
        arrivalAirport: normalizeText(getArrivalAirport(flight)),
        departureTime: formatUtcTime(timeInfo.departureTime),
        arrivalTime: formatUtcTime(timeInfo.arrivalTime),
        rawDepartureTime: timeInfo.departureTime,
        rawArrivalTime: timeInfo.arrivalTime,
    };
}

function buildTextOverviewCell(field, values, counterpartValues, counterpartRoleLabel, { compact = false } = {}) {
    const changed = normalizeComparableText(values[field.key]) !== normalizeComparableText(counterpartValues[field.key]);
    return {
        key: field.key,
        label: compact ? null : field.label,
        value: values[field.key],
        changed,
        diffValue: !compact && changed ? `${counterpartRoleLabel} ${counterpartValues[field.key]}` : null,
    };
}

function buildTimeOverviewCell(field, values, diffMinutes, { compact = false, showDiff = true } = {}) {
    const diffValue = showDiff ? formatSignedTimeDiff(diffMinutes) : null;
    return {
        key: field.key,
        label: compact ? null : field.label,
        value: values[field.key],
        changed: Boolean(diffValue),
        diffValue,
        diffTone: diffValue ? getTimeDiffDirection(diffMinutes) : null,
    };
}

function buildCompareOverviewRow({ key, label, tone, values, counterpartValues, counterpartRoleLabel, departureDiff, arrivalDiff, compact = false, showTimeDiffs = true }) {
    return {
        key,
        label,
        tone,
        cells: [
            ...COMPARE_OVERVIEW_TEXT_FIELDS.map((field) => buildTextOverviewCell(field, values, counterpartValues, counterpartRoleLabel, { compact })),
            buildTimeOverviewCell(COMPARE_OVERVIEW_TIME_FIELDS[0], values, departureDiff, { compact, showDiff: showTimeDiffs }),
            buildTimeOverviewCell(COMPARE_OVERVIEW_TIME_FIELDS[1], values, arrivalDiff, { compact, showDiff: showTimeDiffs }),
        ],
    };
}

function buildCompareOverviewRows(editingFlight, referenceFlight, { compact = false } = {}) {
    if (!editingFlight || !referenceFlight) {
        return [];
    }
    const editingValues = buildCompareOverviewValues(editingFlight, false);
    const referenceValues = buildCompareOverviewValues(referenceFlight, true);
    const departureDiff = getMinutesDiff(editingValues.rawDepartureTime, referenceValues.rawDepartureTime);
    const arrivalDiff = getMinutesDiff(editingValues.rawArrivalTime, referenceValues.rawArrivalTime);

    return [
        buildCompareOverviewRow({
            key: "reference",
            label: compact ? null : "执行航班",
            tone: "reference",
            values: referenceValues,
            counterpartValues: editingValues,
            counterpartRoleLabel: "草稿",
            departureDiff,
            arrivalDiff,
            compact,
            showTimeDiffs: !compact,
        }),
        buildCompareOverviewRow({
            key: "editing",
            label: compact ? null : "草稿航班",
            tone: "editing",
            values: editingValues,
            counterpartValues: referenceValues,
            counterpartRoleLabel: "基线",
            departureDiff,
            arrivalDiff,
            compact,
            showTimeDiffs: true,
        }),
    ];
}

function normalizeComparableText(value, fallback = "--") {
    return hasValue(value) ? String(value).trim().toUpperCase() : fallback;
}

function formatAircraftDiff(editingFlight, referenceFlight) {
    const editingAircraft = normalizeComparableText(editingFlight?.aircraft_reg, "未分配");
    const referenceAircraft = normalizeComparableText(referenceFlight?.aircraft_reg, "未分配");
    return editingAircraft === referenceAircraft ? `一致 ${editingAircraft}` : `${editingAircraft} vs ${referenceAircraft}`;
}

function formatRouteCompact(flight) {
    return `${normalizeComparableText(getDepartureAirport(flight))}->${normalizeComparableText(getArrivalAirport(flight))}`;
}

function formatRouteDiff(editingFlight, referenceFlight) {
    const editingRoute = formatRouteCompact(editingFlight);
    const referenceRoute = formatRouteCompact(referenceFlight);
    return editingRoute === referenceRoute ? `一致 ${editingRoute}` : `${editingRoute} vs ${referenceRoute}`;
}

function getReferenceExecutionStatus(flight) {
    return String(flight?.source_metadata?.status || flight?.status || "").toUpperCase();
}

function getExecutionStatusLabel(status) {
    const normalizedStatus = String(status || "").toUpperCase();
    return EXECUTION_STATUS_LABELS[normalizedStatus] || (normalizedStatus ? normalizedStatus : "状态待确认");
}

function hasExecutionStatusRisk(referenceFlight) {
    const status = getReferenceExecutionStatus(referenceFlight);
    return Boolean(status && !EXECUTION_STATUS_SAFE_FOR_MATCH.has(status));
}

function formatExecutionStatusRisk(referenceFlight) {
    const status = getReferenceExecutionStatus(referenceFlight);
    const label = getExecutionStatusLabel(status);
    return hasExecutionStatusRisk(referenceFlight) ? `${label}（风险）` : label;
}

function getTransactionSummaryTotal(summary) {
    return Number(summary?.total) || 0;
}

function formatTransactionSummary(summary, loading, error) {
    if (loading) {
        return "读取中";
    }
    if (error) {
        return "暂不可用";
    }
    const total = getTransactionSummaryTotal(summary);
    if (!total) {
        return "无当前事务";
    }
    const byType = summary?.by_type || {};
    const byMigrationStatus = summary?.by_migration_status || {};
    const typeParts = Object.entries(byType)
        .filter(([, count]) => Number(count) > 0)
        .map(([type, count]) => `${TRANSACTION_TYPE_LABELS[type] || type} ${count}`);
    const migrationParts = Object.entries(byMigrationStatus)
        .filter(([status, count]) => status !== "NONE" && Number(count) > 0)
        .map(([status, count]) => `${MIGRATION_STATUS_LABELS[status] || "其他状态"} ${count}`);
    return [`事务 ${total}`, ...typeParts, ...migrationParts].join("，");
}

function buildCompareDifferenceRows({ editingFlight, referenceFlight, transactionSummary, transactionSummaryLoading, transactionSummaryError }) {
    if (!editingFlight || !referenceFlight) {
        return [];
    }
    const editingTimeInfo = getFlightTimeDisplayInfo(editingFlight);
    const referenceTimeInfo = getFlightTimeDisplayInfo(referenceFlight, { useEffectiveTime: true });
    const departureDiff = getMinutesDiff(editingTimeInfo.departureTime, referenceTimeInfo.departureTime);
    const arrivalDiff = getMinutesDiff(editingTimeInfo.arrivalTime, referenceTimeInfo.arrivalTime);
    const executionStatusRisk = hasExecutionStatusRisk(referenceFlight);
    const transactionRisk = getTransactionSummaryTotal(transactionSummary) > 0;

    return compactRows([
        buildRow("起飞差异", formatTimeDiffDetail(departureDiff, editingTimeInfo.departureSourceLabel, editingTimeInfo.departureTime, referenceTimeInfo.departureSourceLabel, referenceTimeInfo.departureTime)),
        buildRow("落地差异", formatTimeDiffDetail(arrivalDiff, editingTimeInfo.arrivalSourceLabel, editingTimeInfo.arrivalTime, referenceTimeInfo.arrivalSourceLabel, referenceTimeInfo.arrivalTime)),
        buildRow("飞机差异", formatAircraftDiff(editingFlight, referenceFlight)),
        buildRow("航段差异", formatRouteDiff(editingFlight, referenceFlight)),
        buildRow("执行状态", formatExecutionStatusRisk(referenceFlight), { tone: executionStatusRisk ? "warning" : null }),
        buildRow("事务风险", formatTransactionSummary(transactionSummary, transactionSummaryLoading, transactionSummaryError), { tone: transactionRisk || transactionSummaryError ? "warning" : null }),
    ]);
}

function buildRiskRows(risks, nextStep = "点击航班查看详情") {
    const riskRows = risks.slice(0, 3).map((risk, index) => buildRow(index === 0 ? "关注点" : `风险 ${index + 1}`, risk, {
        key: `risk:${index}:${risk}`,
        tone: "warning",
    }));

    return compactRows([
        ...riskRows,
        buildRow("下一步", nextStep),
    ]);
}

function buildFlightBadges(item, flight, risks) {
    return uniqueTexts([
        getTrackContextBadge(item),
        ...(item?.badges || []).map(normalizeBadgeText),
        getChangeStatusLabel(flight?.change_status),
        flight?.is_fixed ? "固定航班" : null,
        risks.length ? `有约束 ${risks.length}` : null,
    ]).slice(0, 4);
}

function buildFlightPanelModel(target) {
    const item = target?.item;
    const flight = item?.meta?.flight;
    if (!flight) {
        return null;
    }

    const useEffectiveTime = Boolean(item?.meta?.usesEffectiveTime);
    const blockHour = formatMinutes(getFlightBlockTimeMinutes(flight));
    const groundTime = normalizeMinutes(flight.ground_time_minutes);
    const risks = collectRiskLabels(item, flight);
    const badges = buildFlightBadges(item, flight, risks);
    const sections = [
        {
            title: "排班影响",
            rows: compactRows([
                buildRow("飞机", flight.aircraft_reg || flight.aircraftReg || "未分配"),
                buildRow("时间", formatTimeContext(flight, useEffectiveTime)),
                buildRow("飞行时间", blockHour ? `BH ${blockHour}` : null),
                buildRow("下一段", groundTime === null ? null : `GT ${formatMinutes(groundTime)}`),
            ]),
        },
        {
            title: "风险与下一步",
            rows: buildRiskRows(risks),
        },
    ];

    return {
        mode: target?.mode || "timeline",
        kind: "flight",
        tone: item?.variant || "flight",
        title: formatFlightIdentity(flight),
        subtitle: formatTimeContext(flight, useEffectiveTime),
        badges,
        sections,
    };
}

function buildGroundPanelModel(target) {
    const item = target?.item;
    const ground = item?.meta?.ground;
    if (!ground) {
        return null;
    }

    const title = ground.isContinuous ? normalizeText(ground.airportCode, "机场") : "机场不连续";
    const airportLabel = ground.isContinuous
        ? normalizeText(ground.airportCode, "机场")
        : `${normalizeText(ground.arrivalAirport)}->${normalizeText(ground.departureAirport)}`;
    const actualGroundTime = normalizeMinutes(ground.groundTimeMinutes);
    const minGroundTime = normalizeMinutes(ground.minGroundTimeMinutes);
    const diffMinutes = normalizeMinutes(ground.groundTimeDiffMinutes)
        ?? (actualGroundTime !== null && minGroundTime !== null ? actualGroundTime - minGroundTime : null);
    const compactLine = [
        `${airportLabel} 过站 ${formatCompactMinutes(actualGroundTime)}`,
        minGroundTime !== null ? `最小 ${formatCompactMinutes(minGroundTime)}` : null,
        formatSignedCompactMinutes(diffMinutes),
    ].filter(Boolean).join(" · ");

    return {
        mode: target?.mode || "timeline",
        kind: "ground",
        tone: ground.isContinuous ? "ground" : "warning",
        title,
        subtitle: compactLine,
        badges: [],
        compactLine,
        sections: [],
    };
}

function getAircraftAvailabilityTypeLabel(windowType) {
    const normalizedType = String(windowType || "OTHER").toUpperCase();
    return AIRCRAFT_AVAILABILITY_TYPE_LABELS[normalizedType] || AIRCRAFT_AVAILABILITY_TYPE_LABELS.OTHER;
}

function getAircraftAvailabilityStatusLabel(status) {
    const normalizedStatus = String(status || "ACTIVE").toUpperCase();
    return AIRCRAFT_AVAILABILITY_STATUS_LABELS[normalizedStatus] || normalizedStatus;
}

function formatAvailabilityWindowRange(availabilityWindow) {
    const start = formatUtcTime(availabilityWindow?.start_time);
    const end = availabilityWindow?.end_time ? formatUtcTime(availabilityWindow.end_time) : "直到另行通知";
    return `${start} -> ${end}`;
}

function buildAircraftAvailabilityPanelModel(target) {
    const item = target?.item;
    const availabilityWindow = item?.meta?.availabilityWindow;
    if (!availabilityWindow) {
        return null;
    }

    const typeLabel = item?.meta?.windowTypeLabel || getAircraftAvailabilityTypeLabel(availabilityWindow.window_type);
    const statusLabel = getAircraftAvailabilityStatusLabel(availabilityWindow.status);
    const aircraftReg = item?.meta?.aircraftReg || availabilityWindow.aircraft_reg || "未记录飞机";
    const title = normalizeText(item?.meta?.fullDisplayLabel || item?.fullDisplayLabel || item?.title || availabilityWindow.title || typeLabel, "飞机不可用");
    const subtitle = [aircraftReg, formatAvailabilityWindowRange(availabilityWindow)].filter(Boolean).join(" · ");

    return {
        mode: target?.mode || "timeline",
        kind: "aircraft-availability",
        tone: "warning",
        title,
        subtitle,
        badges: uniqueTexts(["飞机可用性", typeLabel, statusLabel]).slice(0, 4),
        sections: [
            {
                title: "不可用窗口",
                rows: compactRows([
                    buildRow("飞机", aircraftReg),
                    buildRow("类型", typeLabel),
                    buildRow("时间", formatAvailabilityWindowRange(availabilityWindow)),
                    buildRow("机场", availabilityWindow.airport_code),
                    buildRow("状态", statusLabel),
                ]),
            },
            {
                title: "说明",
                rows: compactRows([
                    buildRow("备注", availabilityWindow.remarks),
                    buildRow("下一步", "调整航班时间或更换飞机，避免与不可用窗口重叠", { tone: "warning" }),
                ]),
            },
        ],
    };
}

function getMatchStatus(match, publicationReview = false) {
    if (!match) {
        return "UNMATCHED";
    }
    if (!publicationReview && match.is_valid === false) {
        return "INVALID";
    }
    return String((publicationReview ? match.match_status : match.effective_status || match.match_status) || "UNMATCHED").toUpperCase();
}

function getMatchType(match) {
    return String(match?.match_type || "AUTO").toUpperCase();
}

function getMatchStatusLabel(match, publicationReview = false) {
    const status = getMatchStatus(match, publicationReview);
    if (publicationReview) {
        return PUBLICATION_STATUS_LABELS[status] || "发布快照缺失";
    }
    return MATCH_STATUS_LABELS[status] || "待确认";
}

function getMatchDecisionSourceLabel(match) {
    return match ? MATCH_TYPE_LABELS[getMatchType(match)] || "决策待确认" : "待人工确认";
}

function getPublicationMeaning(status, role, publicationReview = false) {
    if (publicationReview) {
        if (status === "MATCHED") {
            return "发布时已和执行基线匹配，用编辑侧结果替换原执行航班。";
        }
        if (status === "NEW") {
            return "发布时创建了新的执行航班。";
        }
        if (status === "CANCELLED") {
            return "发布时取消了参考侧原执行航班。";
        }
        if (status === "IGNORE") {
            return "发布时跳过该航班，不创建也不取消执行航班。";
        }
        return "发布快照或匹配事实不完整，需要回到发布记录核对。";
    }
    if (status === "MATCHED") {
        return "已找到发布对象，发布时用编辑侧结果更新参考基线航班。";
    }
    if (status === "NEW") {
        return "编辑侧作为新增航班发布，会创建新的执行航班。";
    }
    if (status === "CANCELLED") {
        return "参考侧作为取消对象发布，会取消旧执行航班。";
    }
    if (status === "IGNORE") {
        return role === "reference" ? "参考侧本次不取消，发布会跳过该执行航班。" : "编辑侧本次不新增，发布会跳过该航班。";
    }
    if (status === "INVALID") {
        return "原匹配已失效，发布前需要重新确认对象。";
    }
    return "尚未确认发布身份，发布前需要在匹配表处理。";
}

function getCompareDiffSummary(match) {
    return match?.diff_summary
        || match?.difference_summary
        || match?.reason
        || match?.invalid_reason
        || null;
}

function buildCompareBadges({ match, statusLabel, role, publicationReview }) {
    return uniqueTexts([
        publicationReview ? "发布回看" : "发布确认",
        role === "reference" ? "参考基线" : role === "editing" ? "编辑侧" : null,
        statusLabel,
        match ? getMatchDecisionSourceLabel(match) : null,
    ]).slice(0, 4);
}

function buildCompareRiskRows({ match, status, publicationReview, referenceFlight, transactionSummary, transactionSummaryLoading, transactionSummaryError }) {
    const executionStatus = getReferenceExecutionStatus(referenceFlight);
    const transactionTotal = getTransactionSummaryTotal(transactionSummary);
    const risks = compactRows([
        buildRow("关注点", status === "INVALID" ? (match?.invalid_reason || "匹配已失效，需重新处理") : null, { tone: "warning" }),
        buildRow("关注点", executionStatus && hasExecutionStatusRisk(referenceFlight) ? `执行航班状态为${getExecutionStatusLabel(executionStatus)}，发布前需复核。` : null, { key: "execution-status-risk", tone: "warning" }),
        buildRow("关注点", transactionTotal ? `执行航班存在 ${transactionTotal} 个事务，发布前需评估迁移风险。` : null, { key: "transaction-risk", tone: "warning" }),
        buildRow("关注点", transactionSummaryError ? "事务摘要暂不可用，需稍后重试或在匹配表核对。" : null, { key: "transaction-error", tone: "warning" }),
        buildRow("关注点", transactionSummaryLoading ? "事务摘要仍在读取中。" : null, { key: "transaction-loading" }),
        buildRow("关注点", status === "UNMATCHED" ? "发布身份未确认，发布前不能依赖该关系。" : null, { key: "unmatched", tone: "warning" }),
        buildRow("差异", getCompareDiffSummary(match), { tone: status === "INVALID" ? "warning" : null }),
    ]);

    return compactRows([
        ...risks,
        buildRow("下一步", publicationReview ? "点击航班查看发布详情" : "在匹配表处理或点击航班查看详情"),
    ]);
}

function buildCompareLinkPanelModel(target) {
    const match = target?.match || null;
    const publicationReview = Boolean(target?.publicationReview);
    const status = getMatchStatus(match, publicationReview);
    const statusLabel = getMatchStatusLabel(match, publicationReview);
    const editingFlight = target?.editingItem?.meta?.flight;
    const referenceFlight = target?.referenceItem?.meta?.flight;
    const executionStatusRisk = hasExecutionStatusRisk(referenceFlight);
    const transactionRisk = getTransactionSummaryTotal(target?.transactionSummary) > 0;
    const transactionSummaryText = formatTransactionSummary(target?.transactionSummary, target?.transactionSummaryLoading, target?.transactionSummaryError);
    const compactMeta = uniqueTexts([
        getMatchType(match) === "MANUAL" ? getMatchDecisionSourceLabel(match) : null,
        status === "MATCHED" ? null : statusLabel,
        status === "INVALID" ? (match?.invalid_reason || "匹配已失效") : null,
        referenceFlight && executionStatusRisk ? formatExecutionStatusRisk(referenceFlight) : null,
        transactionRisk || target?.transactionSummaryError || target?.transactionSummaryLoading ? transactionSummaryText : null,
    ]);

    return {
        mode: "compare",
        kind: "compare-link",
        tone: "compare",
        title: statusLabel,
        subtitle: [formatFlightSummary(referenceFlight), formatFlightSummary(editingFlight)].filter(Boolean).join(" -> ") || statusLabel,
        badges: [],
        hideHeader: true,
        compactMeta,
        comparisonRows: buildCompareOverviewRows(editingFlight, referenceFlight, { compact: true }),
        sections: [],
    };
}

function buildComparePanelModel(target) {
    if (target?.kind === "link") {
        return buildCompareLinkPanelModel(target);
    }

    const match = target?.match || target?.item?.meta?.match || null;
    const publicationReview = Boolean(target?.publicationReview || target?.item?.meta?.publicationReview);
    const status = getMatchStatus(match, publicationReview);
    const statusLabel = getMatchStatusLabel(match, publicationReview);
    const role = target?.item?.meta?.role;
    const useEffectiveTime = Boolean(target?.item?.meta?.usesEffectiveTime);
    const focusFlight = target?.item?.meta?.flight;
    const oppositeFlight = target?.oppositeItem?.meta?.flight;
    const editingFlight = target?.editingItem?.meta?.flight;
    const referenceFlight = target?.referenceItem?.meta?.flight;
    const differenceRows = buildCompareDifferenceRows({
        editingFlight,
        referenceFlight,
        transactionSummary: target?.transactionSummary,
        transactionSummaryLoading: target?.transactionSummaryLoading,
        transactionSummaryError: target?.transactionSummaryError,
    });
    const compareRows = compactRows([
        buildRow("发布含义", getPublicationMeaning(status, role, publicationReview), { tone: status === "INVALID" ? "warning" : null }),
        buildRow("匹配对象", formatFlightSummary(oppositeFlight)),
        buildRow("匹配来源", getMatchDecisionSourceLabel(match)),
        target?.kind === "link" ? buildRow("编辑侧", formatFlightSummary(editingFlight)) : null,
        target?.kind === "link" ? buildRow("参考侧", formatFlightSummary(referenceFlight)) : null,
    ]);
    const title = target?.kind === "link"
        ? "匹配关系"
        : focusFlight
            ? formatFlightIdentity(focusFlight)
            : "匹配关系";
    const subtitle = focusFlight
        ? formatTimeContext(focusFlight, useEffectiveTime)
        : [formatFlightSummary(editingFlight), formatFlightSummary(referenceFlight)].filter(Boolean).join(" ↔ ") || statusLabel;

    return {
        mode: "compare",
        kind: target?.kind === "link" ? "compare-link" : "compare-flight",
        tone: "compare",
        title,
        subtitle,
        badges: buildCompareBadges({ match, statusLabel, role, publicationReview }),
        sections: [
            { title: "发布判断", rows: compareRows },
            { title: "差异详情", rows: differenceRows },
            { title: "风险与下一步", rows: buildCompareRiskRows({ match, status, publicationReview, referenceFlight, transactionSummary: target?.transactionSummary, transactionSummaryLoading: target?.transactionSummaryLoading, transactionSummaryError: target?.transactionSummaryError }) },
        ],
    };
}

export function buildScheduleDraftTimelineHoverPanelModel(target) {
    if (!target) {
        return null;
    }
    if (target.mode === "compare") {
        return buildComparePanelModel(target);
    }
    const item = target.item;
    if (item?.itemType === "aircraft-availability" || item?.meta?.contextType === "aircraft-availability") {
        return buildAircraftAvailabilityPanelModel(target);
    }
    if (item?.itemType === "ground" || item?.meta?.itemType === "ground") {
        return buildGroundPanelModel(target);
    }
    return buildFlightPanelModel(target);
}

const ScheduleDraftTimelineHoverPanel = ({ target, hidden = false }) => {
    const layoutFrameRef = useRef(null);
    const targetMode = target?.mode;
    const targetKind = target?.kind;
    const targetItem = target?.item;
    const targetMatch = target?.match;
    const targetOppositeItem = target?.oppositeItem;
    const targetEditingItem = target?.editingItem;
    const targetReferenceItem = target?.referenceItem;
    const targetPublicationReview = target?.publicationReview;
    const targetTransactionSummary = target?.transactionSummary;
    const targetTransactionSummaryLoading = target?.transactionSummaryLoading;
    const targetTransactionSummaryError = target?.transactionSummaryError;
    const targetTrackRowHeight = target?.trackRowHeight;
    const targetAnchorElement = target?.anchorElement;
    const rawTargetAnchorRect = target?.anchorRect;
    const [layoutState, setLayoutState] = useState(() => ({
        viewportSize: getBrowserViewportSize(),
        anchorRect: null,
        anchorElement: null,
        rawAnchorRect: null,
    }));
    const model = useMemo(
        () => (hidden ? null : buildScheduleDraftTimelineHoverPanelModel({
            mode: targetMode,
            kind: targetKind,
            item: targetItem,
            match: targetMatch,
            oppositeItem: targetOppositeItem,
            editingItem: targetEditingItem,
            referenceItem: targetReferenceItem,
            publicationReview: targetPublicationReview,
            transactionSummary: targetTransactionSummary,
            transactionSummaryLoading: targetTransactionSummaryLoading,
            transactionSummaryError: targetTransactionSummaryError,
        })),
        [
            hidden,
            targetMode,
            targetKind,
            targetItem,
            targetMatch,
            targetOppositeItem,
            targetEditingItem,
            targetReferenceItem,
            targetPublicationReview,
            targetTransactionSummary,
            targetTransactionSummaryError,
            targetTransactionSummaryLoading,
        ],
    );
    const viewportPoint = normalizeViewportPoint(target?.viewportPoint);
    const targetAnchorRect = normalizeViewportRect(rawTargetAnchorRect);
    const anchorRect = layoutState.anchorElement === targetAnchorElement && layoutState.rawAnchorRect === rawTargetAnchorRect
        ? layoutState.anchorRect || targetAnchorRect
        : targetAnchorRect;

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }
        const updateLayoutState = () => {
            const nextSize = getBrowserViewportSize();
            const nextAnchorRect = getAnchorElementViewportRect(targetAnchorElement) || normalizeViewportRect(rawTargetAnchorRect);
            setLayoutState((currentState) => {
                const sameViewport = currentState.viewportSize.width === nextSize.width && currentState.viewportSize.height === nextSize.height;
                const sameAnchor = areViewportRectsEqual(currentState.anchorRect, nextAnchorRect);
                const sameSource = currentState.anchorElement === targetAnchorElement && currentState.rawAnchorRect === rawTargetAnchorRect;
                return sameViewport && sameAnchor && sameSource
                    ? currentState
                    : {
                        viewportSize: nextSize,
                        anchorRect: nextAnchorRect,
                        anchorElement: targetAnchorElement || null,
                        rawAnchorRect: rawTargetAnchorRect || null,
                    };
            });
        };
        const scheduleLayoutStateUpdate = () => {
            if (layoutFrameRef.current !== null) {
                return;
            }
            layoutFrameRef.current = window.requestAnimationFrame(() => {
                layoutFrameRef.current = null;
                updateLayoutState();
            });
        };
        updateLayoutState();
        window.addEventListener("resize", scheduleLayoutStateUpdate);
        window.addEventListener("scroll", scheduleLayoutStateUpdate, true);
        const resizeObserver = targetAnchorElement && typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(scheduleLayoutStateUpdate)
            : null;
        resizeObserver?.observe(targetAnchorElement);
        return () => {
            if (layoutFrameRef.current !== null) {
                window.cancelAnimationFrame(layoutFrameRef.current);
                layoutFrameRef.current = null;
            }
            resizeObserver?.disconnect();
            window.removeEventListener("resize", scheduleLayoutStateUpdate);
            window.removeEventListener("scroll", scheduleLayoutStateUpdate, true);
        };
    }, [targetAnchorElement, rawTargetAnchorRect, hidden]);

    if (!model || (!anchorRect && !viewportPoint)) {
        return null;
    }

    const position = calculateScheduleDraftTimelineHoverPanelPosition({
        anchorRect,
        viewportPoint,
        viewportSize: layoutState.viewportSize,
        trackRowHeight: targetTrackRowHeight,
        verticalStrategy: targetMode === "timeline" ? HOVER_PANEL_VERTICAL_STRATEGY_NEAR_ANCHOR : HOVER_PANEL_VERTICAL_STRATEGY_AVOID_VIEWPORT,
    });
    const panelStyle = {
        left: position.left,
        top: position.top,
        width: position.width,
        maxHeight: position.maxHeight ? position.maxHeight : undefined,
    };

    const panelClassName = [
        "schedule-draft-hover-panel",
        `schedule-draft-hover-panel-${model.mode}`,
        `schedule-draft-hover-panel-${model.kind}`,
        model.compactLine ? "schedule-draft-hover-panel-compact-mode" : null,
        model.hideHeader ? "schedule-draft-hover-panel-no-header" : null,
    ].filter(Boolean).join(" ");
    const visibleSections = model.sections.filter((section) => section.rows.length);
    const hasBodyContent = Boolean(model.compactMeta?.length || model.comparisonRows?.length || visibleSections.length);

    return (
        <aside
            className={panelClassName}
            style={panelStyle}
            data-horizontal-placement={position.horizontalPlacement}
            data-vertical-placement={position.verticalPlacement}
            aria-live="polite"
        >
            {model.compactLine ? <div className="schedule-draft-hover-panel-compact-line">{model.compactLine}</div> : null}
            {!model.compactLine && !model.hideHeader ? (
                <div className="schedule-draft-hover-panel-header">
                    <div>
                        <div className="schedule-draft-hover-panel-eyebrow">{model.mode === "compare" ? "匹配速览" : "时间线速览"}</div>
                        <div className="schedule-draft-hover-panel-title">{model.title}</div>
                        <div className="schedule-draft-hover-panel-subtitle">{model.subtitle}</div>
                    </div>
                    <div className="schedule-draft-hover-panel-badges">
                        {model.badges.slice(0, 3).map((badge) => (
                            <span key={badge} className="schedule-draft-hover-panel-badge">{badge}</span>
                        ))}
                    </div>
                </div>
            ) : null}
            {hasBodyContent ? <div className="schedule-draft-hover-panel-body">
                {model.compactMeta?.length ? (
                    <div className="schedule-draft-hover-panel-compact-meta">
                        {model.compactMeta.map((value) => <span key={value}>{value}</span>)}
                    </div>
                ) : null}
                {model.comparisonRows?.length ? (
                    <div className="schedule-draft-hover-panel-compare-rows" aria-label="匹配航班对照">
                        {model.comparisonRows.map((row) => (
                            <div key={row.key} className={`schedule-draft-hover-panel-compare-row schedule-draft-hover-panel-compare-row-${row.tone || row.key}${row.label ? "" : " schedule-draft-hover-panel-compare-row-no-label"}`}>
                                {row.label ? <div className="schedule-draft-hover-panel-compare-row-label">{row.label}</div> : null}
                                <div className="schedule-draft-hover-panel-compare-cells">
                                    {row.cells.map((cell) => (
                                        <div key={cell.key} className={`schedule-draft-hover-panel-compare-cell${cell.changed ? " schedule-draft-hover-panel-compare-cell-changed" : ""}${cell.diffTone ? ` schedule-draft-hover-panel-compare-cell-diff-${cell.diffTone}` : ""}`}>
                                            {cell.label ? <span>{cell.label}</span> : null}
                                            <strong>{cell.value}</strong>
                                            {cell.diffValue ? <em>{cell.diffValue}</em> : null}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}
                {visibleSections.map((section) => (
                    <section key={section.title} className="schedule-draft-hover-panel-section">
                        <div className="schedule-draft-hover-panel-section-title">{section.title}</div>
                        {section.rows.map((row) => (
                            <div key={row.key || `${section.title}:${row.label}`} className={`schedule-draft-hover-panel-row${row.tone ? ` schedule-draft-hover-panel-row-${row.tone}` : ""}`}>
                                <span>{row.label}</span>
                                <strong>{row.value}</strong>
                            </div>
                        ))}
                    </section>
                ))}
            </div> : null}
        </aside>
    );
};

export default ScheduleDraftTimelineHoverPanel;
